
import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface MultiDestinationProps {
  destinations: { address: string; amount: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: 'address' | 'amount', value: string) => void;
  totalAmount: string;
}

const MultiDestination: React.FC<MultiDestinationProps> = ({
  destinations,
  onAdd,
  onRemove,
  onChange,
  totalAmount
}) => {
  // Calculate remaining amount based on allocated amounts
  const allocatedAmount = destinations.reduce((sum, dest) => {
    const amount = parseFloat(dest.amount) || 0;
    return sum + amount;
  }, 0);
  
  const remainingAmount = Math.max(0, parseFloat(totalAmount) - allocatedAmount).toFixed(3);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Multiple Destinations</h3>
        <div className="text-xs text-muted-foreground">
          <span className={remainingAmount === '0.000' ? 'text-whirlpool-cyan' : 'text-amber-400'}>
            {remainingAmount} SOL
          </span> remaining to allocate
        </div>
      </div>
      
      <div className="space-y-3">
        {destinations.map((dest, index) => (
          <div key={index} className="flex gap-3 items-end">
            <div className="flex-1">
              <Label htmlFor={`dest-${index}`} className="text-xs mb-1 block">
                Address {index + 1}
              </Label>
              <Input
                id={`dest-${index}`}
                placeholder="Enter Solana address"
                value={dest.address}
                onChange={(e) => onChange(index, 'address', e.target.value)}
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="w-24">
              <Label htmlFor={`amount-${index}`} className="text-xs mb-1 block">
                Amount
              </Label>
              <div className="relative">
                <Input
                  id={`amount-${index}`}
                  type="number"
                  min="0.001"
                  step="0.001"
                  placeholder="0.0"
                  value={dest.amount}
                  onChange={(e) => onChange(index, 'amount', e.target.value)}
                  className="bg-secondary border-white/10 pr-12"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
                  SOL
                </div>
              </div>
            </div>
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              onClick={() => onRemove(index)}
              className="mb-0.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      
      {destinations.length < 10 && (
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={onAdd}
          className="w-full bg-secondary border-white/10 hover:bg-white/10 hover:text-whirlpool-cyan"
          disabled={destinations.length >= 10}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Destination
        </Button>
      )}
    </div>
  );
};

export default MultiDestination;
